import {
  LazyQueryExecFunction,
  MutationFunctionOptions,
  useLazyQuery,
  useMutation,
  useQuery,
} from '@apollo/client';
import { Turnstile } from '@marsidev/react-turnstile';
import {
  BookingForPayBalanceDocument,
  CountryCodes,
  TransactionAddressInput,
  TrustMyTravelAuthorisationCodeDocument,
  TrustMyTravelAuthorisationCodeQuery,
  TrustMyTravelAuthorisationCodeQueryVariables,
} from '@website/types/graphqlOperations';
import Link from 'next/link';
import Router, { useRouter } from 'next/router';
import { useState } from 'react';
import { Alert, Col, Form, Row } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import useGeolocation from '../../hooks/geoLocation/useGeolocation';
import {
  BookingForPayBalanceQuery,
  Currency,
  PayBalanceDocument,
  PayBalanceInput,
  PayBalanceMutation,
} from '../../types/graphqlOperations';
import Address from '../forms/fields/Address';
import { SubmitButton } from '../forms/fields/SubmitButton';
import ErrorPage from '../utilities/ErrorPage';
import LoadingPage from '../utilities/LoadingPage';
import { addressLineOneContainsLetters } from './BookingForm';
import {
  PayBalancePrices,
  PayBalancePricesSubmitValues,
} from './subComponents/PayBalancePrices';
import PaymentFormLayout from './subComponents/PaymentFormLayout';
import TMTStyle from './tmt/TMTStyle';
import { useTMTScript } from './tmt/script';
import setupModal from './tmt/setupModal';
import { Transaction } from './tmt/types';
import { tmtErrorAlert } from './utils/tmtErrorAlert';
/**
 * Format the mutation input
 *
 * @param values Form values
 * @param booking Booking
 * @param transaction Spreedly transaction authorize response
 */
export function formatMutationInput(
  values: PayBalanceOnSubmitValues,
  booking: BookingForPayBalanceQuery['booking'],
  transaction?: Pick<Transaction, 'bookings' | 'id'>,
): PayBalanceInput {
  return {
    amount: {
      amountZD: parseInt(values.amount.amountZD),
      currency: values.amount.currency as Currency,
    },
    bookingID: booking.id,
    card: {
      address: values.address as any,
    },
    tmtBookingID: transaction!.bookings[0].id,
    tmtTransactionID: transaction!.id,
  };
}

/**
 * Pay Balance Form submit handler
 *
 * @param values Form values
 * @param Apollo Client
 * @param booking Booking
 * @param payBalanceMutation Pay balance mutation
 * @param setError React hook form error callback
 */
export async function payBalanceSubmit(
  values: PayBalanceOnSubmitValues,
  tmtAuthLazyQuery: LazyQueryExecFunction<
    TrustMyTravelAuthorisationCodeQuery,
    TrustMyTravelAuthorisationCodeQueryVariables
  >,
  booking: BookingForPayBalanceQuery['booking'],
  payBalanceMutation: (
    options?: MutationFunctionOptions<
      PayBalanceMutation,
      { input: PayBalanceInput }
    >,
  ) => Promise<any>,
  setSuccess: (v: boolean) => void,
  setProcessing: (v: boolean) => void,
  setError: (v?: string) => void,
) {
  setProcessing(true);
  setError(undefined);

  // Scroll to the top of the page
  window.scroll({
    top: 0,
    left: 0,
    behavior: 'smooth',
  });

  // Check the first line of the address includes letters as well as numbers (TMT requires this)
  if (!addressLineOneContainsLetters(values)) {
    alert(
      'The first line of your address must contain letters as well as numbers.',
    );
    setProcessing(false);
    return;
  }

  // Launch the TMT Modal
  const tmtPaymentModal = await setupModal({
    tmtAuthLazyQuery,
    currency: booking.spaces[0].guestTotal.currency,
    endDate: booking.group.end.utc,
    trip: booking.trip,
    user: booking.user,
    values: {
      prices: { payment: parseInt(values.amount.amountZD) },
      address: values.address,
      turnstileToken: values.turnstileToken,
    },
  });

  // Handle close
  tmtPaymentModal.on('modal_closed', function handleClose() {
    setProcessing(false);
  });

  // Handle navigating away
  const { router } = Router;
  router.events.on('routeChangeComplete', () => {
    document.querySelectorAll('._tmt--container').forEach((el) => {
      el.remove();
    });
  });

  // Handle token expired
  tmtPaymentModal.on('token_error', function handleTokenError(err) {
    console.log('TOKEN ERROR');
    console.log(err);

    // Close the modal (as it otherwise just shows a message of 'token expired' and doesn't have an option to retry)
    tmtPaymentModal.closeModal();

    // Open it again
    payBalanceSubmit(
      values,
      tmtAuthLazyQuery,
      booking,
      payBalanceMutation,
      setSuccess,
      setProcessing,
      setError,
    );
  });

  // Handle transaction failed
  tmtPaymentModal.on(
    'transaction_failed',
    function handleTransactionError(data) {
      console.log('TRANSACTION FAILED');
      console.log(data);

      // Close the modal (as it otherwise just shows a message of declined and doesn't have an option to try again)
      tmtPaymentModal.closeModal();

      // Open it again
      payBalanceSubmit(
        values,
        tmtAuthLazyQuery,
        booking,
        payBalanceMutation,
        setSuccess,
        setProcessing,
        setError,
      );

      // Alert with the details
      tmtErrorAlert(data);
    },
  );

  // Handle transaction error (e.g. network error)
  tmtPaymentModal.on('transaction_error', function handleTransactionError(err) {
    console.log('TRANSACTION ERROR');
    console.log(err);

    // Close the modal (as it otherwise just shows a message of declined and doesn't have an option to try again)
    tmtPaymentModal.closeModal();

    // Open it again
    payBalanceSubmit(
      values,
      tmtAuthLazyQuery,
      booking,
      payBalanceMutation,
      setSuccess,
      setProcessing,
      setError,
    );

    // Alert with the details
    setError('Lost internet connection. Please try again.');
  });

  tmtPaymentModal.on(
    'transaction_timeout',
    function handleTransactionTimeout(data) {
      console.error('TRANSACTION TIMEOUT');
      console.error(data);

      // Close the modal (as it otherwise just shows a message of declined and doesn't have an option to try again)
      tmtPaymentModal.closeModal();

      // Log booking ID. Lookup booking to establish if has a status of paid or not
      const bookingId = data?.booking_id;

      setError(
        `Transaction timed out. Please contact support with booking ID: ${bookingId}`,
      );
    },
  );

  tmtPaymentModal.on('booking_logged', function handleTransactionTimeout(data) {
    console.log('BOOKING LOGGED');
    console.log(data);
  });

  tmtPaymentModal.on('booking_exists', function handleTransactionTimeout(data) {
    console.error('BOOKING LOGGED');
    console.error(data);

    // Close the modal (as it otherwise just shows a message of declined and doesn't have an option to try again)
    tmtPaymentModal.closeModal();

    alert(`Something went wrong. Please contact us.`);
  });

  tmtPaymentModal.on('booking_error', function handleTransactionTimeout(data) {
    console.error('PAY BALANCE ERROR');
    console.error(data);
  });

  // transaction_result_available
  tmtPaymentModal.on(
    'transaction_result_available',
    function handleTransactionTimeout(data) {
      console.log('TRANSACTION RESULT AVAILABLE');
      console.log(data);
    },
  );

  // close_window_attempted
  tmtPaymentModal.on(
    'close_window_attempted',
    function handleTransactionTimeout(data) {
      console.error('CLOSE WINDOW ATTEMPTED');
      console.error(data);

      // Close the modal (as it otherwise just shows a message of declined and doesn't have an option to try again)
      tmtPaymentModal.closeModal();

      alert(`Something went wrong. Please contact us.`);
    },
  );

  // Handle success
  tmtPaymentModal.on(
    'transaction_logged',
    async function handleTransactionResponse(transactionResponse: Transaction) {
      // Log the transaction success
      console.log('TRANSACTION SUCCESS');

      // Close the modal so we just see the loading indicator
      tmtPaymentModal.closeModal();
      setProcessing(true);

      // Format the mutation object
      const input = formatMutationInput(values, booking, transactionResponse);

      // Submit mutation
      try {
        await payBalanceMutation({ variables: { input } });
        setSuccess(true);
      } catch (error) {
        // Handle errors
        console.log('TRANSACTION API ERROR');
        console.log(error);
        // eslint-disable-next-line no-alert
        alert(error);
        setProcessing(false);
      }

      // Close the modal
      tmtPaymentModal.closeModal();
    },
  );
}

/**
 * Pay Balance Form Inner
 *
 * Contains the form itself, to be shown once the booking data has loaded.
 */
export function PayBalanceFormInner({
  booking,
}: {
  booking: BookingForPayBalanceQuery['booking'];
}) {
  const { push } = useRouter();
  const geoLocation = useGeolocation();
  const [error, setError] = useState<string>();

  // Load the TMT script
  const [tmtLoading, tmtLoadingError] = useTMTScript();

  // Setup form state
  const { handleSubmit, register, watch, control } =
    useForm<PayBalanceOnSubmitValues>({
      defaultValues: {
        address: {
          country: geoLocation?.country?.code || CountryCodes.GB,
        },
      },
    });

  // Setup success & processing state
  const [processing, setProcessing] = useState<boolean>(false);

  // Setup the TMT auth query (but don't run yet)
  const [tmtAuthLazyQuery] = useLazyQuery(
    TrustMyTravelAuthorisationCodeDocument,
    {
      fetchPolicy: 'network-only',
    },
  );

  // Setup mutation function
  const [payBalanceMutation] = useMutation(PayBalanceDocument);

  // Wait for TMT to load
  if (tmtLoading) {
    return (
      <>
        <LoadingPage />
      </>
    );
  }

  if (tmtLoadingError) {
    return (
      <>
        <ErrorPage error="Failed to load" />
      </>
    );
  }

  // If submitted (but not yet paid)
  if (processing) {
    return (
      <>
        <TMTStyle />
        <div style={{ minHeight: '100vh' }}>
          <LoadingPage />
        </div>
      </>
    );
  }

  // Otherwise show the form
  return (
    <>
      <PaymentFormLayout
        group={{
          ...booking.group,
          trip: booking.trip,
        }}
      >
        <Form
          onSubmit={handleSubmit((values) =>
            payBalanceSubmit(
              values,
              tmtAuthLazyQuery,
              booking,
              payBalanceMutation,
              () => {
                push(
                  `/me/${booking.user.id}/bookings/${booking.id}/pay/success`,
                );
              },
              setProcessing,
              setError,
            ),
          )}
        >
          <TMTStyle />

          {error && (
            <Alert variant="danger">
              <Alert.Heading>Payment Failed</Alert.Heading>
              <p>{error}</p>
              <p>If the error persists, please contact us. </p>
              <Link href="/help/contact" passHref legacyBehavior>
                <Alert.Link>Contact Us</Alert.Link>
              </Link>
            </Alert>
          )}

          <PayBalancePrices booking={booking} register={register} />

          <h2 className="h4">Billing Address</h2>
          <Address
            watch={watch}
            register={register}
            name="address"
            localityRequired
            postalCodeRequired
          />

          <Controller
            control={control}
            name="turnstileToken"
            rules={{
              required: 'Please complete the captcha',
            }}
            render={({ field }) => (
              <Row>
                <Col xs={'auto'} className="mx-auto py-3">
                  <Turnstile
                    siteKey={
                      process.env.NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_SITE_ID || ''
                    }
                    onSuccess={(token) => {
                      field.onChange(token);
                    }}
                  />
                </Col>
              </Row>
            )}
          />

          <SubmitButton isSubmitting={false} text="Next" className="my-3" />
        </Form>
      </PaymentFormLayout>
    </>
  );
}

/**
 * Pay Balance Form
 */
export default function PayBalanceForm({ bookingID }: { bookingID: string }) {
  // Get the booking
  const { data, loading } = useQuery(BookingForPayBalanceDocument, {
    variables: { id: bookingID },
    fetchPolicy: 'network-only',
  });

  if (loading) return <LoadingPage />;

  return <PayBalanceFormInner booking={data.booking} />;
}

/**
 * On submit values type
 *
 * Combination of all fields
 */
export type PayBalanceOnSubmitValues = {
  amount: PayBalancePricesSubmitValues;
  turnstileToken: string;
} & {
  address: TransactionAddressInput;
};
